import { CloseOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Checkbox,
  Col,
  ConfigProvider,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputProps,
  Modal,
  ModalProps,
  Row,
} from 'antd';
import { Rule } from 'antd/es/form';
import { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';
import { ReactNode, memo, useEffect, useState } from 'react';

import { GET_REGION } from 'apis/GraphQL/Queries/customerQuery';
import { ITripCheckIndexNum, ITripParams } from 'apis/GraphQL/Variables/trip';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import TripAPI from 'apis/TripApis';
import { Button, SelectForm } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import {
  IAreaForm,
  ICity,
  IDistrict,
  ISaleLineArea,
  ISaleLineForm,
  ISaleLineItemData,
  ITrip,
  ITripForm,
} from 'interfaces/SaleLine';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import {
  convertDateToBE,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';

type IProps = {
  data: ISaleLineItemData[];
  isEdit: boolean;
  loading: boolean;
  setEditIndex: (data: any) => void;
  onSubmit: (data: any, isCreateTrip: boolean) => void;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
} & ModalProps;

interface IRenderTable {
  label?: string;
  rules?: Rule[];
  blankCol?: boolean;
  inputProps: InputProps;
  component?: ReactNode;
}

interface IModal {
  open?: boolean;
  dataSource: any;
  title: ReactNode;
  className?: string;
  cancelTitle: string;
  submitTitle: string;
  classNames?: ModalProps['classNames'];
  form: FormInstance<any>;
  columns: ColumnsTypeCustom;
  handleCancel?: () => void;
  handleSubmit?: () => void;
}

interface IPrevTrip {
  tripId: string;
  indexNum: string;
}

const tripDataSource = [
  {
    id: '',
    routeId: '', // add routeid to avoid table key error
    tripCode: '',
    tripName: '',
  },
];

const areaDataSource = [
  {
    id: '',
    routeId: '', // add routeid to avoid table key error
    tcity: '',
    tdistrict: '',
    tward: '',
  },
];

const initNewArea: ISaleLineArea = {
  id: '',
  cityId: '',
  cityName: '',
  wardId: '',
  wardName: '',
  districtId: '',
  districtName: '',
  cityNewId: '',
  wardNewId: '',
};

export const optionStatus: DefaultOptionType[] = [
  { label: 'Hoạt động', value: 'R100_01' },
  { label: 'Không hoạt động', value: 'R100_02' },
];

const { PlusOutlined } = icons;

const ModalSaleLinePage = ({
  data,
  isEdit,
  loading,
  onSubmit,
  setEditIndex,
  setIsOpenModal,
  ...props
}: IProps) => {
  const [tripForm] = Form.useForm<ITripForm>(),
    [areaForm] = Form.useForm<IAreaForm>(),
    [saleLineForm] = Form.useForm<ISaleLineForm>();

  const { user } = useUser();

  const [isChecked, setIsChecked] = useState(false),
    [isOpenAreaModal, setIsOpenAreaModal] = useState(false),
    [isOpenTripModal, setIsOpenTripModal] = useState(false);

  const [cities, setCities] = useState<ICity[]>([]),
    [prevTrip, setPrevTrip] = useState<IPrevTrip>(),
    [areas, setAreas] = useState<ISaleLineArea[]>([]),
    [districts, setDistricts] = useState<IDistrict[]>([]),
    [newArea, setNewArea] = useState<ISaleLineArea>(initNewArea),
    [optionTrip, setOptionTrip] = useState<DefaultOptionType[]>([]),
    [optionWard, setOptionWard] = useState<DefaultOptionType[]>([]);

  const { mutate: getListCity } = useMutation({
      mutationFn: (params: IParamQraphQL) =>
        axiosGraphQL.post('/graphql', params),
      onSuccess: (res) => {
        const items = res.data?.getRegion?.items || [];
        const citiesMap: { [key: string]: string } = {};
        items.forEach((item: any) => {
          const cityName = item.cityName || item.tcity?.name;
          if (item.cityId && cityName) {
            citiesMap[item.cityId] = cityName;
          }
        });
        const cityOptions = Object.keys(citiesMap).map((id) => ({
          name: citiesMap[id],
          id: id,
          note: null,
          provinceId: null,
          tdistricts: [],
        }));

        setCities(cityOptions);
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { mutate: getListWardByCity } = useMutation({
      mutationFn: (params: IParamQraphQL) =>
        axiosGraphQL.post('/graphql', params),
      onSuccess: (res) => {
        const regions = res.data.getRegion?.items || [];

        setOptionWard(
          regions.map((item: any) => ({
            value: item.wardId,
            label: item.wardName || item.tward?.name,
          })),
        );
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { mutate: getListTrip } = useMutation({
      mutationFn: () => TripAPI.getListTrip({}),
      onSuccess: (res) => {
        const trips: ITrip[] = res.data.getTripM.items;

        setOptionTrip(
          trips.map((trip) => ({
            label: trip.id,
            value: trip.name,
          })),
        );
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const { mutateAsync: checkExistedIndexNum } = useMutation({
      mutationFn: (params: ITripCheckIndexNum) =>
        TripAPI.checkExistedIndexNum(params),
    }),
    { mutate: createTripByPlantId } = useMutation({
      mutationFn: (params: ITripParams) => TripAPI.createTripByPlantId(params),
      onSuccess: (res) => {
        if (!res.data.createTripM) return;
        const data = res.data.createTripM;

        tripForm.resetFields();
        handleResetFieldValues('tripId', data.id);
        handleResetFieldValues('tripName', data.name);

        getListTrip();
        setIsOpenTripModal(false);
        toastSuccess('Tạo chuyến thành công');
      },
      onError: (error) => {
        console.log(error);
        toastError('Tạo chuyến thất bại, mã tuyến đã tồn tại');
      },
    });

  useEffect(() => {
    if (!props.open) return;

    try {
      const dataItem = data[0],
        { validFromDate, validToDate, area } = dataItem;

      const fieldValues: ISaleLineForm = {
        ...dataItem,
        validFromDate: validFromDate ? dayjs(validFromDate) : dayjs(new Date()),
        validToDate: validToDate ? dayjs(validToDate) : '',
        area: area ? area : ([] as ISaleLineArea[]),
      };

      const tareas: ISaleLineArea[] = area.map((item) => ({
        ...item,
        cityId: '',
        wardId: '',
        wardName: item.wardName ? item.wardName : initNewArea.wardName,
        districtId: item.districtId ? item.districtId : initNewArea.districtId,
        districtName: item.districtName
          ? item.districtName
          : initNewArea.districtName,
        cityNewId: item.cityNewId || item.cityId,
        wardNewId: item.wardNewId || item.wardId,
        cityNameNew: item.cityNameNew || '',
        wardNameNew: item.wardNameNew || '',
        isLegacy: !item.cityNewId,
      }));

      setAreas(tareas);
      setIsChecked(!!dataItem.tripId);
      setDistricts(districts);
      setPrevTrip({
        tripId: dataItem.tripId,
        indexNum: dataItem.indexNum,
      });
      saleLineForm.setFieldsValue(fieldValues);
    } catch (error) {
      setAreas([]);
      setDistricts([]);
      setIsChecked(false);
      saleLineForm.setFieldsValue(data[0]);
    }
  }, [props.open, cities]);

  useEffect(() => {
    if (!props.open) return;

    getListTrip();
    getListCity({
      query: GET_REGION,
      variables: {
        take: 5000,
      },
    });
  }, [props.open]);

  useEffect(() => {
    if (isOpenAreaModal) return;

    setDistricts([]);
    setOptionWard([]);
  }, [isOpenAreaModal]);

  const handleResetFieldValues = (
    field: keyof ISaleLineItemData,
    value: any,
  ) => {
    const fieldsValue = saleLineForm.getFieldsValue();

    saleLineForm.setFieldsValue({
      ...fieldsValue,
      [field]: value,
    });
  };

  const renderTable = ({
    label,
    rules,
    blankCol,
    component,
    inputProps,
  }: IRenderTable) => {
    const { name, value } = inputProps,
      customRules = rules !== undefined ? rules : ([] as Rule[]);

    return (
      <Form.Item
        name={name}
        label={label}
        className="m-0"
        rules={customRules}
        initialValue={value}
        labelCol={{ span: 24, style: { padding: '0' } }}
      >
        {!blankCol ? (
          component ? (
            component
          ) : (
            <Input value={value} className="rounded-xl h-10" {...inputProps} />
          )
        ) : (
          <div className="w-full"></div>
        )}
      </Form.Item>
    );
  };

  const tripColumns: ColumnsTypeCustom = [
    {
      dataIndex: 'tripCode',
      className: 'w-full',
      render: (value) => {
        return renderTable({
          inputProps: {
            value: value,
            name: 'tripCode',
            placeholder: 'Nhập mã chuyến',
          },
          label: 'Mã chuyến',
          rules: [{ required: true, message: 'Nhập mã chuyến' }],
        });
      },
    },
    {
      dataIndex: 'tripName',
      className: 'w-full',
      render: (value) => {
        return renderTable({
          inputProps: {
            value: value,
            name: 'tripName',
            placeholder: 'Nhập tên chuyến',
          },
          label: 'Tên chuyến',
          rules: [{ required: true, message: 'Nhập tên chuyến' }],
        });
      },
    },
  ];

  const saleTripCols: ColumnsTypeCustom = [
    {
      dataIndex: 'tripId',
      className: 'basis-1/2 pr-3',
      render: () => {
        const handleChangeTripSelect = (_: string, option: any) => {
          const fomatedOption = !option
            ? { label: undefined, value: undefined }
            : option;

          handleResetFieldValues('tripId', fomatedOption.label);
          handleResetFieldValues('tripName', fomatedOption.value);
        };

        return (
          isChecked &&
          renderTable({
            inputProps: {
              name: 'tripId',
            },
            label: 'Mã chuyến',
            blankCol: !isChecked,
            rules: [{ required: true, message: 'Chọn mã chuyến' }],
            component: (
              <SelectForm
                size="middle"
                className="h-10"
                options={optionTrip}
                placeholder="Chọn mã chuyến"
                onChange={handleChangeTripSelect}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      className="px-3 py-[5px] text-primary-60 font-bold cursor-pointer hover:opacity-50"
                      onClick={() => setIsOpenTripModal(true)}
                    >
                      Tạo mới
                    </div>
                  </>
                )}
              />
            ),
          })
        );
      },
    },
    {
      dataIndex: 'tripName',
      className: 'basis-1/2 pl-3',
      render: (value) => {
        return (
          isChecked &&
          renderTable({
            inputProps: {
              value: value,
              disabled: true,
              name: 'tripName',
            },
            label: 'Tên chuyến',
            blankCol: !isChecked,
          })
        );
      },
    },
    {
      dataIndex: 'indexNum',
      className: 'basis-1/2 pr-3',
      render: (value) => {
        const validateNumber = async (_: any, value: string) => {
          if (!value) {
            return Promise.reject('Nhập thứ tự trong chuyến');
          }

          const numberValue = Number(value);
          if (isNaN(numberValue)) {
            return Promise.reject('Vui lòng nhập một số');
          }

          if (numberValue <= 0) {
            return Promise.reject('Giá trị phải lớn hơn 0');
          }

          if (!Number.isInteger(numberValue)) {
            return Promise.reject('Giá trị phải là số nguyên');
          }
        };

        return (
          isChecked &&
          renderTable({
            inputProps: {
              value: value,
              type: 'number',
              name: 'indexNum',
              placeholder: 'Nhập thứ tự trong chuyến',
            },
            blankCol: !isChecked,
            label: 'Thứ tự trong chuyến',
            rules: [{ validator: validateNumber }],
          })
        );
      },
    },
  ];

  const areaColumns: ColumnsTypeCustom = [
    {
      dataIndex: 'tcity',
      className: 'basis-1/2 pr-3',
      render: () => {
        const handleChangeCitySelect = (value: any, option: any) => {
          areaForm.setFieldValue('tward', undefined);
          setOptionWard([]);

          if (!option) return;

          const tcity: Pick<
            ISaleLineArea,
            'cityId' | 'cityName' | 'cityNewId'
          > = {
            cityId: '', // Ngừng lưu vào field cũ
            cityName: option.label,
            cityNewId: option.value,
          };
          setNewArea({ ...initNewArea, ...tcity });

          getListWardByCity({
            query: GET_REGION,
            variables: {
              input: {
                cityId: {
                  equals: value,
                },
              },
              take: 1000,
            },
          });
        };

        return renderTable({
          inputProps: {
            name: 'tcity',
          },
          label: 'Tỉnh/Thành phố',
          component: (
            <SelectForm
              options={cities.map((city) => ({
                label: city.name,
                value: city.id,
              }))}
              onChange={handleChangeCitySelect}
            />
          ),
          rules: [{ required: true, message: 'Chọn Tỉnh/Thành phố' }],
        });
      },
    },
    {
      dataIndex: 'tward',
      className: 'basis-1/2 pl-3',
      render: () => {
        const handleChangeSelectWard = (_: any, option: any) => {
          const formatedOption = option ? option : { label: '', value: '' };
          const tward: Pick<
            ISaleLineArea,
            'wardId' | 'wardName' | 'wardNewId'
          > = {
            wardId: '', // Ngừng lưu vào field cũ
            wardName: formatedOption.label,
            wardNewId: formatedOption.value,
          };

          setNewArea((prev) => ({ ...prev, ...tward }));
        };

        return renderTable({
          label: 'Phường/Xã',
          inputProps: { name: 'tward' },
          component: (
            <SelectForm
              options={optionWard}
              onChange={handleChangeSelectWard}
            />
          ),
          rules: [{ required: true, message: 'Chọn Phường/Xã' }],
        });
      },
    },
  ];

  const saleLinecolumns: ColumnsTypeCustom = [
    {
      dataIndex: 'routeId',
      className: 'basis-1/2 pr-3',
      render: (value) => {
        return renderTable({
          inputProps: {
            value: value,
            name: 'routeId',
            disabled: isEdit,
            placeholder: 'Mã tuyến',
          },
          label: 'Mã tuyến',
          rules: [
            { required: true, message: 'Nhập mã tuyến' },
            !isEdit
              ? { max: 7, message: 'Mã tuyến không được vượt quá 7 ký tự' }
              : {},
          ],
        });
      },
    },
    {
      dataIndex: 'routeName',
      className: 'basis-1/2 pl-3',
      render: (value) => {
        return renderTable({
          inputProps: {
            value: value,
            name: 'routeName',
            placeholder: 'Tên tuyến',
          },
          label: 'Tên tuyến',
          rules: [{ required: true, message: 'Nhập tên tuyến' }],
        });
      },
    },
    {
      title: '',
      className: 'basis-full',
      render: () => {
        return renderTable({
          inputProps: {},
          component: (
            <Button
              type="button"
              title="Khu vực"
              iconBefore={<PlusOutlined />}
              handleOnclick={() => setIsOpenAreaModal(true)}
            />
          ),
        });
      },
    },
    ...(areas.length > 0
      ? areas.map((area, index) => {
          return {
            key: index,
            dataIndex: 'area',
            className: `basis-1/2 ${index % 2 === 0 ? 'pr-3' : 'pl-3'}`,
            render: () => {
              let newAreaText = '';
              let oldAreaText = '';

              if (area.isLegacy) {
                if (area.cityName && area.cityName.trim() !== '') {
                  oldAreaText =
                    area.wardName && area.wardName.trim() !== ''
                      ? `${area.wardName}, ${area.cityName}`
                      : area.cityName;
                }
              } else if (area.cityNameNew && area.cityNameNew.trim() !== '') {
                newAreaText =
                  area.wardNameNew && area.wardNameNew.trim() !== ''
                    ? `${area.wardNameNew}, ${area.cityNameNew}`
                    : area.cityNameNew;
                if (area.cityName && area.cityName.trim() !== '') {
                  oldAreaText =
                    area.wardName && area.wardName.trim() !== ''
                      ? `${area.wardName}, ${area.cityName}`
                      : area.cityName;
                }
              } else {
                if (area.cityName && area.cityName.trim() !== '') {
                  newAreaText =
                    area.wardName && area.wardName.trim() !== ''
                      ? `${area.wardName}, ${area.cityName}`
                      : area.cityName;
                }
              }

              let displayValue = '';
              if (newAreaText && oldAreaText) {
                displayValue = `[Mới] ${newAreaText} | [Cũ] ${oldAreaText}`;
              } else if (newAreaText) {
                displayValue = newAreaText;
              } else if (oldAreaText) {
                displayValue = `[Cũ] ${oldAreaText}`;
              } else {
                displayValue = '-';
              }

              const handleClear = () => {
                const newAreas = areas.filter(
                  (a) =>
                    !(
                      (a.cityNewId || a.cityId) ===
                        (area.cityNewId || area.cityId) &&
                      (a.wardNewId || a.wardId) ===
                        (area.wardNewId || area.wardId)
                    ),
                );

                setAreas(newAreas);
              };

              return renderTable({
                inputProps: {
                  value: displayValue,
                  suffix: displayValue ? (
                    <CloseOutlined
                      className="cursor-pointer text-red-500 p-1"
                      onClick={handleClear}
                    />
                  ) : null,
                },
                label: 'Khu vực',
                rules: [{ required: true, message: 'Thêm khu vực' }],
              });
            },
          };
        })
      : [
          {
            dataIndex: 'route',
            className: 'basis-1/2 pr-3',
            render: () => {
              return renderTable({
                inputProps: {
                  name: 'route',
                  readOnly: true,
                  placeholder: 'Bấm nút để tạo khu vực',
                },
                label: 'Khu vực',
                rules: [{ required: true, message: 'Thêm khu vực' }],
              });
            },
          },
        ]),
    ...(areas.length % 2 !== 0 || areas.length === 0
      ? [
          {
            className: 'basis-1/2 pl-3',
            render: () => {
              return renderTable({
                inputProps: {},
                blankCol: true,
              });
            },
          },
        ]
      : []),
    {
      dataIndex: 'numExpectedDays',
      className: 'basis-1/2 pr-3',
      render: (value) => {
        const validateNumber = async (_: any, value: string) => {
          if (!value) return Promise.resolve();

          const numberValue = Number(value);
          if (isNaN(numberValue)) return Promise.reject('Vui lòng nhập một số');

          if (numberValue <= 0) return Promise.reject('Giá trị phải lớn hơn 0');

          if (!Number.isInteger(numberValue))
            return Promise.reject('Giá trị phải là số nguyên');
        };

        return renderTable({
          inputProps: {
            value: value,
            type: 'number',
            name: 'numExpectedDays',
            placeholder: 'Nhập thời gian',
          },
          label: 'Thời gian tuyến',
          rules: [{ validator: validateNumber }],
        });
      },
    },
    {
      dataIndex: 'statusId',
      className: 'basis-1/2 pl-3',
      render: () => {
        const handleChangeStateSelect = (value: any) => {
          handleResetFieldValues('statusId', value);
        };

        return renderTable({
          inputProps: {
            name: 'statusId',
          },
          label: 'Trạng thái',
          component: (
            <SelectForm
              disabled={!isEdit}
              options={optionStatus}
              onChange={handleChangeStateSelect}
            />
          ),
        });
      },
    },
    {
      dataIndex: 'validFromDate',
      className: 'basis-1/2 pr-3',
      render: () => {
        const disabledDate = (currentDate: dayjs.Dayjs) => {
          const today = new Date(),
            yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);

          return dayjs(currentDate).toISOString() < yesterday.toISOString();
        };

        return renderTable({
          inputProps: {
            name: 'validFromDate',
          },
          label: 'Ngày hoạt động',
          component: (
            <DatePicker
              picker="date"
              allowClear={false}
              inputReadOnly={true}
              className="w-full h-10"
              disabledDate={disabledDate}
              placeholder="Chọn ngày hoạt động"
              format={{ format: 'DD/MM/YYYY' }}
              onChange={() => handleResetFieldValues('validToDate', undefined)}
            />
          ),
        });
      },
    },
    {
      dataIndex: 'validToDate',
      className: 'basis-1/2 pl-3',
      render: () => {
        // const disabledDate = (currentDate: dayjs.Dayjs) => {
        //   const dateActive = saleLineForm.getFieldValue('validFromDate'),
        //     formatedDateActive =
        //       dateActive === null || dateActive === ''
        //         ? new Date()
        //         : dateActive;

        //   return dayjs(currentDate).isBefore(
        //     dayjs(formatedDateActive).startOf('day'),
        //     'day',
        //   );
        // };

        return renderTable({
          inputProps: {
            name: 'validToDate',
          },
          label: 'Ngày kết thúc',
          component: (
            <DatePicker
              picker="date"
              disabled={true}
              // allowClear={false}
              // inputReadOnly={true}
              className="w-full h-10"
              // disabledDate={disabledDate}
              // placeholder="Chọn ngày kết thúc"
              placeholder=""
              format={{ format: 'DD/MM/YYYY' }}
            />
          ),
        });
      },
    },
    {
      dataIndex: '',
      className: 'basis-full',
      render: (record) => {
        if (record?.tripId) setIsChecked(true);

        return renderTable({
          inputProps: {},
          component: (
            <Checkbox
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            >
              Có trong chuyến
            </Checkbox>
          ),
        });
      },
    },
    ...(isChecked ? saleTripCols : []),
  ];

  const checkFormValidate = async (form: FormInstance<any>) => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSaleLineSubmit = async () => {
    if (!user) return;

    const isValid = await checkFormValidate(saleLineForm);
    if (!isValid) {
      return;
    }

    const fields = saleLineForm.getFieldsValue();
    handleResetFieldValues('area', areas);

    const isTripChange =
      isChecked &&
      (prevTrip?.tripId !== fields.tripId ||
        prevTrip?.indexNum !== fields.indexNum);

    if (isTripChange) {
      const response = await checkExistedIndexNum({
        tripId: fields.tripId,
        indexNum: Number(fields.indexNum),
      });

      if (response.data.getTripDetail.items.length > 0) {
        saleLineForm.setFields([
          {
            name: 'indexNum',
            errors: ['Thứ tự chuyến đã tồn tại'],
          },
        ]);

        return;
      }
    }

    const formatedValidFromDate = dayjs(fields.validFromDate),
      formatedValidToDate = fields.validToDate ? dayjs(fields.validToDate) : '';

    const fieldsValue: ISaleLineForm = {
      ...fields,
      area: areas,
      validFromDate: convertDateToBE(formatedValidFromDate),
      ...(formatedValidToDate && {
        validToDate: convertDateToBE(formatedValidToDate),
      }),
    };
    onSubmit(fieldsValue, isChecked);
  };

  const handleSaleLineCancel = () => {
    setEditIndex(-1);
    setIsChecked(false);
    setIsOpenModal(false);
    setIsOpenTripModal(false);

    saleLineForm.resetFields();
  };

  const handleTripSubmit = async () => {
    if (!user) return;

    if (!(await checkFormValidate(tripForm))) {
      tripForm.validateFields();
      return;
    }

    tripForm.validateFields();
    const fields = tripForm.getFieldsValue();

    const params: ITripParams = {
      id: fields.tripCode,
      name: fields.tripName,
      plantId: user.plantCode,
    };

    createTripByPlantId(params);
  };

  const handleTripCancel = () => {
    setIsOpenTripModal(false);
    tripForm.resetFields();
  };

  const handleAreaSubmit = async () => {
    if (!(await checkFormValidate(areaForm))) {
      areaForm.validateFields();
      return;
    }

    const isExistedItem =
      areas.filter((area) => {
        const areaCityId = area.cityNewId || area.cityId;
        const newCityId = newArea.cityNewId || newArea.cityId;
        const areaWardId = area.wardNewId || area.wardId;
        const newWardId = newArea.wardNewId || newArea.wardId;
        return areaCityId === newCityId && areaWardId === newWardId;
      }).length > 0;

    if (isExistedItem) {
      areaForm.setFields([
        { name: 'tcity', errors: ['Khu vực này đã tồn tại'] },
      ]);
      return;
    }

    setAreas((prev) => [...prev, newArea]);
    setNewArea(initNewArea);

    setIsOpenAreaModal(false);
    areaForm.resetFields();
  };

  const handleAreaCancel = () => {
    setIsOpenAreaModal(false);
    areaForm.resetFields();
  };

  const modals: IModal[] = [
    {
      open: props.open,
      title: props.title,
      cancelTitle: 'Hủy',
      submitTitle: !isEdit ? 'Tạo' : 'Lưu',
      dataSource: data,
      form: saleLineForm,
      columns: saleLinecolumns,
      handleCancel: handleSaleLineCancel,
      handleSubmit: handleSaleLineSubmit,
    },
    {
      open: isOpenTripModal,
      title: 'Tạo chuyến',
      cancelTitle: 'Huỷ',
      submitTitle: 'Tạo chuyến',
      form: tripForm,
      columns: tripColumns,
      dataSource: tripDataSource,
      classNames: {
        footer: '!px-[23px] !py-[15.5px] !bg-rowTable !rounded-b-[8px]',
      },
      handleCancel: handleTripCancel,
      handleSubmit: handleTripSubmit,
    },
    {
      open: isOpenAreaModal,
      title: 'Thêm khu vực',
      cancelTitle: 'Huỷ',
      submitTitle: 'Thêm khu vực',
      form: areaForm,
      columns: areaColumns,
      className: '!w-[558px]',
      dataSource: areaDataSource,
      classNames: {
        footer: '!px-[23px] !py-[15.5px] !bg-rowTable !rounded-b-[8px]',
      },
      handleCancel: handleAreaCancel,
      handleSubmit: handleAreaSubmit,
    },
  ];

  return (
    <>
      {modals.map((modal, index) => {
        const customClassNames: ModalProps['classNames'] = {
          content: '!p-0',
          header:
            '!px-[24px] !pt-[20px] !pb-[8px] !border-sub !border-solid !border-b-[1px]',
          body: '!px-[42px]',
          footer:
            modal.classNames?.footer ||
            '!px-[24px] !py-[20px] !bg-rowTable !rounded-b-[8px]',
        };

        return (
          <Modal
            key={index}
            open={modal.open}
            title={modal.title}
            classNames={customClassNames}
            className={modal.className || '!w-max max-w-[970px]'}
            footer={() => (
              <Row gutter={[8, 16]} justify={'end'} align={'middle'}>
                <Col>
                  <Button
                    fill
                    title={modal.cancelTitle}
                    customClass="!bg-rowTable !border-transparent !text-neutral-30"
                    handleOnclick={modal.handleCancel}
                  />
                </Col>
                <Col>
                  <Button
                    fill
                    title={modal.submitTitle}
                    customClass="w-max max-w-[140px] min-w-[130px] h-[48px]"
                    handleOnclick={modal.handleSubmit}
                  />
                </Col>
              </Row>
            )}
            onCancel={modal.handleCancel}
          >
            <Form form={modal.form}>
              <ConfigProvider
                theme={{
                  components: {
                    Table: {
                      rowHoverBg: '#ffff',
                      borderColor: '#ffff',
                      cellPaddingBlockMD: 8,
                    },
                  },
                }}
              >
                <TableComponent
                  scroll={{}}
                  loading={loading}
                  showHeader={false}
                  pagination={false}
                  columns={modal.columns}
                  dataSource={modal.dataSource}
                  rowClassName={'flex flex-wrap'}
                  rowKey={(record: any) => record.routeId}
                />
              </ConfigProvider>
            </Form>
          </Modal>
        );
      })}
    </>
  );
};

export default memo(ModalSaleLinePage);
